import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { FormProvider } from 'react-hook-form';

import { styled } from '@mui/material/styles';

import { t } from 'i18next';

import { Box, Divider } from '@mui/material';

import {
  Label,
  Button,
  KeyIcon,
  FormTextField,
  FormPhoneField,
  FormPasswordField,
  FormAutocompleteField,
  Typography,
} from '@datanomik/commons-ui';

import { UserShared2Line } from '@datanomik/commons-ui/icons';

import { areaActivityList, clientPositionList } from '../../../services/Form';

import Grid from '../../common/Grid';
import PasswordStrength from '../../common/PasswordStrength';
import Sticky from '../../common/Sticky';

import ContentBox from '../LogIn/ContentBox';

import useUserActivation from './hooks';
import styles from './styles';

const UserActivationRaw = ({ className }) => {
  const { token } = useParams();
  const {
    formMethods,
    passwordPolicies,
    handleOnSubmit,
  } = useUserActivation(token);

  const {
    MIN_LOWERCASE_CHARACTERS,
    MIN_UPPERCASE_CHARACTERS,
    MIN_NUMERIC_CHARACTERS,
    MIN_SPECIAL_CHARACTERS,
    MIN_LENGTH,
  } = passwordPolicies;

  return (
    <Box className={className}>
      <Box className="dn-container">
        <Grid container>
          <Grid item xs={12} md="auto">
            <Sticky position="top" rootSelector="body">
              <ContentBox />
            </Sticky>
          </Grid>
          <Grid item xs={12} md className="dn-flex-center">
            <Box maxWidth="350px" m="auto 0">
              <Grid container>
                <Grid item xs={12} textAlign="center">
                  <KeyIcon size="xlarge" color="neutral"><UserShared2Line /></KeyIcon>
                </Grid>
                <Grid item xs={12} textAlign="center" mb="14px">
                  <Typography variant="h5">
                    {t('User Activation')}
                  </Typography>
                  <Typography variant="p3" color="text.500" mt="4px">
                    {t('Enter your details to active.')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(handleOnSubmit)} noValidate autoComplete="off">
                      <Grid container className="dn-grid-medium">
                        <Grid item xs={12}>
                          <Divider textAlign="left">
                            <Typography>{t('Identity')}</Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={6}>
                          <FormTextField
                            name="firstName"
                            required
                            control={formMethods.control}
                            label={<Label text={t('First Name')} />}
                            labelText={t('First Name')}
                            localeText={{
                              isRequired: t('IS REQUIRED'),
                            }}
                            slotProps={{
                              htmlInput: {
                                autoComplete: 'new-password',
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormTextField
                            name="lastName"
                            required
                            control={formMethods.control}
                            label={<Label text={t('Last Name')} />}
                            labelText={t('Last Name')}
                            localeText={{
                              isRequired: t('IS REQUIRED'),
                            }}
                            slotProps={{
                              htmlInput: {
                                autoComplete: 'new-password',
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormPhoneField
                            name="mobile"
                            required
                            defaultCountry="BR"
                            control={formMethods.control}
                            label={<Label text={t('Mobile')} />}
                            labelText={t('Mobile')}
                            localeText={{
                              isRequired: t('IS REQUIRED'),
                              validPhoneNumber: t('Please enter a valid phone number'),
                              example: t('Example'),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Divider textAlign="left">
                            <Typography>{t('Role')}</Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={12}>
                          <FormAutocompleteField
                            name="areaActivity"
                            required
                            options={areaActivityList.map((option) => ({
                              id: option.value,
                              label: t(option.name),
                            }))}
                            control={formMethods.control}
                            label={<Label text={t('Area activity')} />}
                            labelText={t('Area activity')}
                            localeText={{
                              isRequired: t('IS REQUIRED'),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormAutocompleteField
                            name="position"
                            required
                            options={clientPositionList.map((option) => ({
                              id: option.value,
                              label: t(option.name),
                            }))}
                            control={formMethods.control}
                            label={<Label text={t('Position')} />}
                            labelText={t('Position')}
                            localeText={{
                              isRequired: t('IS REQUIRED'),
                            }}
                          />
                        </Grid>
                        {
                            Object.keys(passwordPolicies).length > 0 && (
                              <>
                                <Grid item xs={12}>
                                  <Divider textAlign="left">
                                    <Typography>{t('Security')}</Typography>
                                  </Divider>
                                </Grid>
                                <Grid item xs={12}>
                                  <FormPasswordField
                                    name="password"
                                    control={formMethods.control}
                                    label={<Label text={t('Password')} />}
                                    labelText={t('Password')}
                                    required
                                    validationRules={{
                                      validate: {
                                        lowerChar: (v) => new RegExp(`^(?=.*[a-z]{${MIN_LOWERCASE_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} lowercase alphabetical characters.', { count: MIN_LOWERCASE_CHARACTERS }),
                                        upperChar: (v) => new RegExp(`^(?=.*[A-Z]{${MIN_UPPERCASE_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} uppercase alphabetical characters.', { count: MIN_UPPERCASE_CHARACTERS }),
                                        numberChar: (v) => new RegExp(`^(?=.*[0-9]{${MIN_NUMERIC_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} numeric characters.', { count: MIN_NUMERIC_CHARACTERS }),
                                        strangeChar: (v) => new RegExp(`^(?=.*[^a-zA-Z0-9]{${MIN_SPECIAL_CHARACTERS},})`).test(v) || t('The password must contain at least {{count}} special characters.', { count: MIN_SPECIAL_CHARACTERS }),
                                        lengthChar: (v) => new RegExp(`^(?=.{${MIN_LENGTH},})`).test(v) || t('The password must be {{count}} characters or longer.', { count: MIN_LENGTH }),
                                      },
                                    }}
                                    localeText={{
                                      isRequired: t('IS REQUIRED'),
                                    }}
                                    slotProps={{
                                      htmlInput: {
                                        autoComplete: 'off',
                                      },
                                    }}
                                  />
                                  <Box p="4px">
                                    <PasswordStrength password={formMethods.watch('password')} />
                                  </Box>
                                </Grid>
                                <Grid item xs={12}>
                                  <FormPasswordField
                                    name="newPassword"
                                    control={formMethods.control}
                                    label={<Label text={t('Repeat Password')} />}
                                    labelText={t('Repeat Password')}
                                    required
                                    validationRules={{
                                      validate: {
                                        passEquals: (v) => v === formMethods.watch('password') || t('Please make sure your passwords match.'),
                                      },
                                    }}
                                    localeText={{
                                      isRequired: t('IS REQUIRED'),
                                    }}
                                    slotProps={{
                                      htmlInput: {
                                        autoComplete: 'off',
                                      },
                                    }}
                                  />
                                </Grid>
                              </>
                            )
                          }
                        <Grid item xs={12}>
                          <Button fullWidth type="submit">
                            {t('Activate')}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </FormProvider>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

UserActivationRaw.propTypes = {
  className: PropTypes.string,
};

export const UserActivation = styled(UserActivationRaw)(styles);

UserActivation.propTypes = UserActivationRaw.propTypes;
